// `.js` on a TypeScript path, which both Vite and `tsc` resolve back to `kinds.ts`. It is
// there because these two modules are the only ones in this package compiled for Node as
// well as bundled, and Node will not resolve an extensionless relative import at runtime.
import { bubbleKind } from './kinds.js';

/**
 * The words a learner may type, offered as buttons instead of remembered as vocabulary.
 *
 * The system has four magic phrases — `more details`, `dispute`, `n more questions`, and "I
 * don't know" — and every one of them exists because the chat network this started on had no
 * buttons. The app does, and the alternative it replaced was a sentence at the bottom of the
 * screen asking somebody to memorise three of them. Nobody memorises a sentence at the
 * bottom of a screen.
 *
 * **These chips are what let the copy stop shouting.** The phrases were printed in capitals
 * everywhere until 2026-09-16 — `MORE DETAILS`, `5 MORE QUESTIONS`, `MÁS DETALLES` — because
 * on WhatsApp that was the only way to mark a word as a word to type, and Leo's verdict on
 * seeing them in a chat bubble was that it *"looks like im yelling"*. What made lowering
 * them cheap is this function: the common path to every one of these is now a thumb, so the
 * text does not have to teach a magic word loudly, only mention it quietly for somebody who
 * prefers typing. The argument in full, and why quotation marks rather than styling, is in
 * `packages/messaging/src/commands.ts`.
 *
 * **A chip fills the box with the bare form and no quotation marks.** Our copy cites a
 * command as `“more details”`; the box gets `more details`, because what goes in the box
 * becomes the learner's own message and nobody quotes themselves. Both parse — every
 * recogniser folds punctuation through `plain` — so this is a choice about how the thread
 * reads, and `i18n.ts` keeps the two forms straight beside the strings.
 *
 * **The cost of offering them is clutter, and clutter is paid for out of the thread.** So
 * the rule is not "show the vocabulary" but "show the ones that make sense right now", which
 * is what this function decides. In practice that is one or two: while a question is open
 * nothing else is offered at all, and the rest are mutually exclusive on the kind of the
 * last message. Three happens in exactly one situation — the last question of the day, just
 * marked — where all three are genuinely the things somebody might want next, and the row
 * scrolls rather than wrapping so even then it costs no height.
 *
 * **A chip fills the box; it does not send.** `dispute` files a complaint against a mark and
 * `n more questions` spends the rest of somebody's day, and neither should be one stray
 * thumb away from happening. Filling the box also leaves the whole point of the two
 * open-ended ones intact: `dispute` is worth most when the learner writes *why* after it
 * (`disputes.ts` — "you told me something different before" is the best dispute a beginner
 * can make), and a chip that sent immediately would throw that sentence away.
 */
export type Suggestion = 'dontKnow' | 'dispute' | 'more' | 'moreQuestions';

export interface Situation {
  /** The newest message from us, whatever it was. `undefined` on an empty thread. */
  lastOutbound?: { direction: 'in' | 'out'; purpose?: string | undefined; text: string } | undefined;
  /** Whether a question is out and unanswered, from the progress panel's `load`. */
  awaitingAnswer: boolean;
  /** Whether the day's questions are all spent, so more have to be asked for. */
  dayIsSpent: boolean;
}

/**
 * Which chips to show, in the order they should appear.
 *
 * Ordered by what the learner is most likely to want, left to right, because the leftmost
 * is the one a thumb reaches without looking. The list is capped by the conditions rather
 * than by a slice: if this ever returns three, the situation genuinely warrants three.
 */
export function suggestionsFor(situation: Situation): Suggestion[] {
  const chips: Suggestion[] = [];
  const kind = situation.lastOutbound ? bubbleKind(situation.lastOutbound) : undefined;

  /*
   * Offered only while a question is actually open.
   *
   * "I don't know" is graded as an admission rather than as a wrong answer
   * (`notknowing.ts`), which is worth something to a learner and worth nothing at all when
   * there is no question outstanding — sent into a quiet thread it earns "that is not a
   * question I asked you", which teaches somebody the buttons lie.
   */
  if (situation.awaitingAnswer) chips.push('dontKnow');

  /*
   * The other three are all suppressed while a question is open, and that is the rule that
   * keeps this from becoming a toolbar.
   *
   * An open question is the one thing the screen is for, and it is also the state a learner
   * is in most of the time. The combination is reachable — the mark for the last answer can
   * still be the newest message we sent while the progress read that says a question is out
   * is up to five minutes newer — and it produced three chips over the box, which is a row
   * of buttons competing with the thing being answered. Nothing is lost: `more details` and
   * `reclamo` still work typed — in any capitalisation, including the shouted forms the copy
   * used to teach — and they come back the moment the question is answered.
   */
  if (!situation.awaitingAnswer) {
    // Against a verdict, and only a verdict. Disputing a briefing is not a thing the system
    // can act on: `disputes.ts` repairs the mark on an item, so there has to be a mark.
    if (kind === 'mark') chips.push('dispute');

    /*
     * `more details` asks for more detail on *the last thing you were taught*, so it is
     * offered when the last thing was teaching: a briefing, or the explanation that comes
     * back with a mark. Not after a question — there is nothing to elaborate yet.
     */
    if (kind === 'briefing' || kind === 'mark') chips.push('more');
  }

  // Only once the day is actually spent. Before that it is a button that says "do the thing
  // that is already happening".
  if (situation.dayIsSpent && !situation.awaitingAnswer) chips.push('moreQuestions');

  // Three at most, by construction, and only at the end of a day that finished on a mark.
  // Asserted in `chat-kinds.test.ts`, because the cost of these is paid out of the height
  // of the conversation and a fourth condition added later would take a line of it silently.

  return chips;
}
